import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './SetupProgress.module.css'

/*
 * Progress ring, extracted from SetupProgress so it can be reused on its own.
 *
 * Second consumer: the FooterNew "More" menu shows this ring as the Setup Score
 * icon once the on-page setup bar retires (first-week window over, or agent
 * deactivated). That cell is ~38px, hence the size/stroke props; `compact`
 * overrides the track/progress/label colours for the smaller, on-white cell.
 *
 * Defaults (size 55 / stroke 4) are the values SetupProgress used inline, so
 * its rendering is unchanged — `.ringWrapper` is already fixed 55x55 in CSS and
 * the inline width/height here resolves to the same 55px.
 */
export const SetupProgressRing = ({
  percent = 0,
  size = 55,
  stroke = 4,
  compact = false,
}) => {
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius

  const [progress, setProgress] = useState(0)

  useEffect(() => {
    // Kick off the animation on mount (next frame so the transition applies).
    const id = requestAnimationFrame(() => setProgress(percent))
    return () => cancelAnimationFrame(id)
  }, [percent])

  const offset = circumference - (progress / 100) * circumference

  return (
    <div
      className={styles.ringWrapper}
      style={{ width: `${size}px`, height: `${size}px` }}
      aria-label={`${Math.round(progress)}% setup complete`}
    >
      <svg width={size} height={size} className={styles.ring}>
        <circle
          className={styles.ringTrack}
          style={compact ? { stroke: '#ece6fb' } : undefined}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          className={styles.ringProgress}
          style={compact ? { stroke: '#6524EB' } : undefined}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span
        className={styles.percentLabel}
        style={compact ? { color: '#6524EB', fontSize: '10px' } : undefined}
      >
        {Math.round(progress)}%
      </span>
    </div>
  )
}

const SetupProgress = ({
  percent = 60,
  title = 'Great Progress!',
  subtitle = 'Complete setup to unlock all features.',
  onContinue,
}) => {
  const navigate = useNavigate()

  // Fall back to the setup-ready screen when no explicit handler is passed.
  const handleContinue = () => {
    if (onContinue) onContinue()
    else navigate('/setup-ready')
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.setupProgress}>
        <SetupProgressRing percent={percent} />

      <div className={styles.textBlock}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      <button type="button" className={styles.continueBtn} onClick={handleContinue}>
        Continue
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className={styles.chevron}
        >
          <path
            d="M9 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      </div>
    </div>
  )
}

export default SetupProgress
